/** The experiments being compared, as removable chips (an archived one badged), and
 *  "Add experiment", which offers active ones only. */

import { sourceName } from "@/features/experiments/status";
import { useRef, useState } from "react";
import { Floating } from "@/components/ui/floating";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useExperiments } from "@/features/experiments/hooks";
import type { ExperimentSummary } from "@/features/experiments/types";
import { formatDate, formatScore } from "@/lib/format";
import { ArchivedBadge } from "@/features/archive/archived";
import { isArchived } from "@/features/archive/kinds";
import { Swatch } from "./series-label";
import { isComparable, MAX_COMPARE, MIN_COMPARE } from "./selection";
import { datasetLabel } from "@/features/experiments/dataset-label";

function AddExperiment({ current, onAdd }: { current: ExperimentSummary[]; onAdd: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  const all = useExperiments({}, open);
  const agents = new Set(current.map((e) => e.agent?.id));
  const taken = new Set(current.map((e) => e.id));
  const candidates = (all.data ?? [])
    .filter((e) => isComparable(e) && !taken.has(e.id))
    .sort((a, b) => Number(agents.has(b.agent?.id)) - Number(agents.has(a.agent?.id)));
  return (
    <div ref={anchor} className="relative">
      <Button
        className="text-link!"
        disabled={current.length >= MAX_COMPARE}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        + Add experiment
      </Button>
      {open && (
        <Floating
          anchor={anchor}
          onDismiss={() => setOpen(false)}
          role="menu"
          aria-label="Experiments to add"
          minWidth={380}
          className="overflow-y-auto"
        >
          {all.isPending && <p className="px-3 py-2.5 text-[13px] text-muted">Loading…</p>}
          {!all.isPending && candidates.length === 0 && (
            <p className="px-3 py-2.5 text-[13px] text-muted">No other finished experiments.</p>
          )}
          {candidates.map((e) => (
            <button
              key={e.id}
              type="button"
              role="menuitem"
              className="flex w-full flex-col items-start gap-0.5 border-t border-line-soft px-3 py-2 text-left first:border-t-0 hover:bg-hover"
              onClick={() => {
                setOpen(false);
                onAdd(e.id);
              }}
            >
              <span className="text-[13px] font-medium">{e.name}</span>
              <span className="text-xs text-muted">
                {sourceName(e)} · {e.dataset ? datasetLabel(e.dataset) : "Trace run"} ·{" "}
                {formatScore(e.aggregate?.overall)} · {formatDate(e.finished_at)}
              </span>
            </button>
          ))}
        </Floating>
      )}
    </div>
  );
}

export function CompareChips({
  experiments,
  onRemove,
  onAdd,
}: {
  experiments: ExperimentSummary[];
  onRemove: (id: string) => void;
  onAdd: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {experiments.map((e, i) => (
        <span
          key={e.id}
          className="inline-flex h-[34px] items-center gap-2 rounded-lg border border-input bg-surface px-2.5 text-[13px]"
        >
          <Swatch index={i} />
          <span className="max-w-[240px] truncate">{e.name}</span>
          {isArchived(e) && <ArchivedBadge />}
          <button
            type="button"
            aria-label={`Remove ${e.name}`}
            disabled={experiments.length <= MIN_COMPARE}
            className="text-faint hover:text-ink disabled:opacity-40"
            onClick={() => onRemove(e.id)}
          >
            <X size={13} strokeWidth={2.2} />
          </button>
        </span>
      ))}
      <AddExperiment current={experiments} onAdd={onAdd} />
    </div>
  );
}
